import React, { useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { deleteShortlist } from '@disco/state-management/actions/shortlist-upload-actions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import { getUser } from '@disco/state-management/selectors/user'

import type { Shortlist } from 'types/shortlist'
import type { User } from 'types/user'

import { getShortlistBaseRoute } from 'utils/routing'

import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import CopyUrlItem from 'components/ui/copy-url-item'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'

import { useDispatch, useSelector } from 'hooks/use-redux'

const ShortlistSaved = (): null | React.ReactNode => {
  const params = useParams<{ shortlistId: string }>()
  const shortlistId = params.shortlistId!
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const shortlist: Shortlist | null | undefined = useSelector(state =>
    getShortlistById(state, {
      id: shortlistId,
    }),
  )
  const user: User = useSelector(getUser)
  const location = useSelector(getLocationBeforeTransitions)
  useEffect(() => {
    if (!shortlist || !shortlist.playlist) {
      const returnPath = location?.state?.returnPath || '/'
      navigate(returnPath)
    }
  }, [dispatch, shortlist, location])
  const closeModal = useCallback(() => {
    dispatch(deleteShortlist(shortlistId))

    // Use returnPath from location state, fallback to home
    const returnPath = location?.state?.returnPath || '/'
    navigate(returnPath)
  }, [dispatch, shortlistId, location])
  const handleCopy = useCallback(() => {
    if (shortlist?.playlist) {
      dispatch(
        sendMixpanelEvent('Playlist saved - Copy public playlist URL', {
          Url: shortlist.playlist.view_playlist_url,
        }),
      )
    }
  }, [dispatch, shortlist])
  const handlePreview = useCallback(() => {
    dispatch(sendMixpanelEvent('Playlist saved - Open public playlist URL'))
  }, [dispatch])
  const showWarning =
    user.business.private_metadata.territories_enabled &&
    user.territories.length > 1
  const shareLink = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    const returnPath = location?.state?.returnPath || '/'

    const pathname = showWarning
      ? `${baseRoute}/shortlists/${shortlistId}/share-warning`
      : `${baseRoute}/shortlists/${shortlistId}/share`

    dispatch(sendMixpanelEvent('Playlist saved - Share this playlist'))
    navigate(pathname, {
      state: { returnPath },
    })
  }, [location, showWarning, shortlistId, dispatch, navigate])
  if (!shortlist || !shortlist.playlist) return null
  // we need at least one track within user territory to enable sharing
  const showShareButton =
    !user.business.private_metadata.territories_enabled ||
    shortlist.items.find(item => !item.track.outside_your_territories)
  return (
    <ModalWindow
      header={<Heading>Playlist Saved!</Heading>}
      actions={
        <ButtonContainer>
          <Button caption="Close" onClick={closeModal} />
          {showShareButton && (
            <div className="SharePlaylist_Btn">
              <Button caption="Share this playlist" onClick={shareLink} />
            </div>
          )}
        </ButtonContainer>
      }
      isOpen
      width="medium"
      onClose={closeModal}
      noMinHeight
    >
      <DocumentHeadTags title="Playlist saved | DISCO" />
      {!shortlist.watermark && (
        <CopyUrlItem
          url={shortlist.playlist?.view_playlist_url ?? ''}
          onCopy={handleCopy}
          onPreview={handlePreview}
        />
      )}
    </ModalWindow>
  )
}

export default ShortlistSaved
